import * as React from "react";
import { useQuery } from "@tanstack/react-query";
import { StatusPill } from "@ozpanel/kit";
import { Section, StateView, useAuth } from "@ozpanel/ui";

import { OperationRunner, type Operation } from "./OperationRunner";

/**
 * Every operation belonging to one domain, grouped by the module that provides it.
 *
 * The per-module tab made all 117 previously-unreachable writes reachable. That is not the same as
 * organised: finding "how do I fail Redis over" required already knowing that Redis is the module
 * which does it, which is precisely the knowledge a console exists to remove the need for. So the
 * operations are placed where the work is — cluster operations under cluster management, mail
 * operations on the mail screen — instead of only under the module that happens to implement them.
 *
 * The grouping is NOT a list kept here. Every module already declares a Category in its manifest
 * and it was used for nothing but sorting the module index; the placement question had been
 * answered by each module, about itself, and nothing read the answer. A module that changes
 * category, or gains a route, moves without anyone editing a console.
 */

type Group = {
  module: string;
  title: string;
  category: string;
  description: string;
  operations: Operation[];
};

export function CategoryOperations({
  category,
  title = "Operations",
  hint,
}: {
  category: string;
  title?: string;
  hint?: string;
}) {
  const { client } = useAuth();
  const q = useQuery({
    queryKey: ["ops-by-category", category],
    queryFn: () =>
      client.apiFetch<{ groups: Group[]; count: number; modules: number; note?: string }>(
        `/ozpanel/operations?category=${encodeURIComponent(category)}`,
      ),
  });

  const groups = q.data?.groups ?? [];

  return (
    <Section
      title={title}
      hint={q.data ? `${q.data.count} across ${q.data.modules} module(s)` : undefined}
      collapsible
      defaultOpen={false}
    >
      <StateView loading={q.isLoading} error={q.error} errorContext="the operations for this area">
        <p className="mb-3 text-[12.5px] leading-relaxed text-muted">
          {hint ??
            "Everything this area can be told to do, built from each module's own API documentation."}{" "}
          Some are normally performed by a cluster step or the reconciler — they are here because the
          moment you need to run one by hand is when the automatic path is what has failed.
        </p>
        {groups.length === 0 ? (
          <div className="rounded-lg border border-line px-3 py-2 text-[12.5px] text-muted">
            No module in this area declares a write operation.
          </div>
        ) : (
          <div className="space-y-4">
            {groups.map((g) => (
              <div key={g.module}>
                <div className="mb-1 flex flex-wrap items-baseline gap-2">
                  <span className="text-[12.5px] font-medium">{g.title}</span>
                  <span className="font-mono text-[11px] text-muted">{g.module}</span>
                  <StatusPill tone="neutral">{g.operations.length}</StatusPill>
                </div>
                <OperationRunner operations={g.operations} />
              </div>
            ))}
          </div>
        )}
      </StateView>
    </Section>
  );
}
